import type {ComponentProps, CSSProperties} from "react";
import clsx from "clsx";
import {cva, type VariantProps} from "class-variance-authority";

const iconVariants = cva("", {
	"variants": {
		"variant": {
			"solid": "fa-solid",
			"regular": "fa-regular",
			"light": "fa-light",
			"thin": "fa-thin",
			"duotone": "fa-duotone",
			"brands": "fa-brands",
			"sharp-solid": "fa-sharp fa-solid",
			"sharp-regular": "fa-sharp fa-regular",
			"sharp-light": "fa-sharp fa-light",
			"sharp-thin": "fa-sharp fa-thin",
			"sharp-duotone": "fa-sharp-duotone fa-solid",
		},
		"size": {
			"1x": "fa-1x",
			"2x": "fa-2x",
			"3x": "fa-3x",
			"4x": "fa-4x",
			"5x": "fa-5x",
			"6x": "fa-6x",
			"7x": "fa-7x",
			"8x": "fa-8x",
			"9x": "fa-9x",
			"10x": "fa-10x",
			"2xs": "fa-2xs",
			"xs": "fa-xs",
			"sm": "fa-sm",
			"lg": "fa-lg",
			"xl": "fa-xl",
			"2xl": "fa-2xl",
		},
		"animation": {
			"beat": "fa-beat",
			"bounce": "fa-bounce",
			"fade": "fa-fade",
			"beat-fade": "fa-beat-fade",
			"flip": "fa-flip",
			"pulse": "fa-pulse",
			"shake": "fa-shake",
			"spin": "fa-spin",
			"spin-pulse": "fa-spin-pulse",
		},
		"spinReverse": {"true": "fa-spin-reverse"},
		"fixedWidth": {"true": "fa-fw"},
		"border": {"true": "fa-border"},
		"pull": {"left": "fa-pull-left", "right": "fa-pull-right"},
		"flip": {"horizontal": "fa-flip-horizontal", "vertical": "fa-flip-vertical", "both": "fa-flip-both"},
		"listItem": {"true": "fa-li"},
		"inverse": {"true": "fa-inverse"},
		"stackDepth": {"1x": "fa-stack-1x", "2x": "fa-stack-2x"},
	},
	"defaultVariants": {
		"variant": "solid",
	},
});

type IconVariantProps = VariantProps<typeof iconVariants>;

type Variant = NonNullable<IconVariantProps["variant"]>;
type Size = NonNullable<IconVariantProps["size"]>;
type Animation = NonNullable<IconVariantProps["animation"]>;

interface FA6IconProps extends Omit<ComponentProps<"i">, "children">, IconVariantProps {
	icon: string;
	/** Degrees. 90/180/270 use the preset classes, any other value rotates freely. */
	rotate?: number;
	/**
	 * Accessible label. When set the icon becomes meaningful (role="img");
	 * when omitted the icon is decorative and hidden from screen readers.
	 */
	title?: string;
}

function FA6Icon({
					 icon,
					 variant,
					 size,
					 animation,
					 spinReverse,
					 fixedWidth,
					 border,
					 pull,
					 flip,
					 rotate,
					 listItem,
					 inverse,
					 stackDepth,
					 title,
					 className,
					 style,
					 ...props
				 }: FA6IconProps) {
	const presetRotate = rotate === 90 || rotate === 180 || rotate === 270;
	const freeRotate = rotate != null && !presetRotate;

	const classes = clsx(
		iconVariants({
			variant,
			size,
			animation,
			spinReverse,
			fixedWidth,
			border,
			pull,
			flip,
			listItem,
			inverse,
			stackDepth,
		}),
		`fa-${icon}`,
		presetRotate && `fa-rotate-${rotate}`,
		freeRotate && "fa-rotate-by",
		className
	);

	const rotateStyle = freeRotate
		? ({"--fa-rotate-angle": `${rotate}deg`} as CSSProperties)
		: undefined;
	const mergedStyle =
		rotateStyle || style ? {...rotateStyle, ...style} : undefined;

	const a11y = title
		? {role: "img", "aria-label": title, title}
		: {"aria-hidden": true as const};

	return <i className={classes} style={mergedStyle} {...a11y} {...props} />;
}

const stackVariants = cva("fa-stack", {
	"variants": {
		"size": {
			"1x": "fa-1x",
			"2x": "fa-2x",
			"3x": "fa-3x",
			"4x": "fa-4x",
			"5x": "fa-5x",
			"6x": "fa-6x",
			"7x": "fa-7x",
			"8x": "fa-8x",
			"9x": "fa-9x",
			"10x": "fa-10x",
			"2xs": "fa-2xs",
			"xs": "fa-xs",
			"sm": "fa-sm",
			"lg": "fa-lg",
			"xl": "fa-xl",
			"2xl": "fa-2xl",
		},
	},
});

interface FA6IconStackProps extends ComponentProps<"span">, VariantProps<typeof stackVariants> {
}

function FA6IconStack({size, className, ...props}: FA6IconStackProps) {
	return <span className={clsx(stackVariants({size}), className)} {...props} />;
}

export {FA6Icon, FA6IconStack};
export type {Variant, Size, Animation};
